/**
 * Discount Code Validation Service
 * Uses Shopify Storefront API to validate discount codes and get actual discounted prices
 */

class DiscountValidator {
  constructor() {
    this.storefrontAccessToken = null;
    this.checkoutId = null;
    this.cachedDiscounts = new Map();
    this.cacheExpiry = 5 * 60 * 1000; // 5 minutes
  }

  /**
   * Initialize the validator with Storefront API token
   * The token should be set in theme settings or passed via Liquid
   */
  init(accessToken) {
    this.storefrontAccessToken = accessToken;
  }

  /**
   * Validate a discount code and get the discounted cart totals
   * @param {string} discountCode - The discount code to validate
   * @param {Array} lineItems - Cart line items in format: [{variantId, quantity, sellingPlanId}]
   * @returns {Promise<Object>} Discount info including amount, type, and discounted total
   */
  async validateDiscount(discountCode, lineItems) {
    if (!discountCode || !lineItems || lineItems.length === 0) {
      return null;
    }

    // Check cache first
    const cacheKey = `${discountCode}_${JSON.stringify(lineItems)}`;
    const cached = this.cachedDiscounts.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.cacheExpiry) {
      console.log('Using cached discount data for:', discountCode);
      return cached.data;
    }

    try {
      // Format line items for Storefront API
      const formattedLineItems = lineItems.map(item => {
        const lineItem = {
          variantId: this.convertToGlobalId(item.variantId, 'ProductVariant'),
          quantity: item.quantity
        };

        // Add selling plan if it exists (for subscriptions)
        if (item.sellingPlanId) {
          lineItem.sellingPlanId = this.convertToGlobalId(item.sellingPlanId, 'SellingPlan');
        }

        return lineItem;
      });

      // Create or update checkout with discount code
      const discountData = await this.createCheckoutWithDiscount(formattedLineItems, discountCode);

      // Cache the result
      this.cachedDiscounts.set(cacheKey, {
        data: discountData,
        timestamp: Date.now()
      });

      return discountData;
    } catch (error) {
      console.error('Error validating discount code:', error);
      return null;
    }
  }

  /**
   * Create a cart with discount code applied using Storefront API
   */
  async createCheckoutWithDiscount(lineItems, discountCode) {
    console.log('[DiscountValidator] Creating cart with:', {
      lineItems,
      discountCode
    });

    // Split line items into subscription and one-time purchase items
    const subscriptionItems = lineItems.filter(item => item.sellingPlanId);
    const oneTimeItems = lineItems.filter(item => !item.sellingPlanId);

    console.log('[DiscountValidator] Split items:', {
      subscriptionCount: subscriptionItems.length,
      oneTimeCount: oneTimeItems.length
    });

    // If we have subscription items, we need to validate without them first
    // because Shopify doesn't allow discount codes on subscription checkouts
    // Map items to correct format: variantId -> merchandiseId
    const itemsForValidation = (oneTimeItems.length > 0 ? oneTimeItems : lineItems).map(item => ({
      merchandiseId: item.variantId,
      quantity: item.quantity
      // Omit sellingPlanId for validation
    }));

    // Use cartCreate mutation (checkoutCreate is deprecated)
    const mutation = `
      mutation cartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            id
            checkoutUrl
            lines(first: 50) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      price {
                        amount
                        currencyCode
                      }
                      compareAtPrice {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
            }
            discountCodes {
              applicable
              code
            }
            discountAllocations {
              discountedAmount {
                amount
                currencyCode
              }
            }
          }
          userErrors {
            code
            field
            message
          }
        }
      }
    `;

    const variables = {
      input: {
        lines: itemsForValidation,
        discountCodes: [discountCode]
      }
    };

    console.log('[DiscountValidator] Request variables:', JSON.stringify(variables, null, 2));

    const response = await this.graphqlRequest(mutation, variables);

    if (response.errors) {
      console.error('[DiscountValidator] GraphQL errors:', response.errors);
      console.error('[DiscountValidator] This usually means:', response.errors.map(e => e.message).join(', '));
      return null;
    }

    const { cart, userErrors } = response.data.cartCreate;

    if (userErrors && userErrors.length > 0) {
      console.warn('[DiscountValidator] Cart errors:', userErrors);
      return {
        valid: false,
        errors: userErrors,
        code: discountCode
      };
    }

    // Parse discount codes
    let discountInfo = {
      valid: false,
      code: discountCode,
      amount: 0,
      type: null,
      percentage: null,
      subtotal: parseFloat(cart.cost.subtotalAmount.amount),
      total: parseFloat(cart.cost.totalAmount.amount),
      currencyCode: cart.cost.totalAmount.currencyCode
    };

    console.log('[DiscountValidator] Cart response:', {
      discountCodes: cart.discountCodes,
      discountAllocations: cart.discountAllocations,
      subtotal: discountInfo.subtotal,
      total: discountInfo.total
    });

    // Check if discount code is applicable
    const appliedDiscount = cart.discountCodes.find(d => d.code === discountCode);

    if (appliedDiscount) {
      console.log('[DiscountValidator] Found discount code:', appliedDiscount);

      // If applicable is true, the discount is valid and applied
      if (appliedDiscount.applicable) {
        discountInfo.valid = true;

        // Calculate total discount amount from allocations
        if (cart.discountAllocations && cart.discountAllocations.length > 0) {
          const totalDiscountAmount = cart.discountAllocations.reduce((sum, allocation) => {
            return sum + parseFloat(allocation.discountedAmount.amount);
          }, 0);

          discountInfo.amount = totalDiscountAmount;

          // Determine if it's percentage or fixed based on the discount amount
          const discountPercentage = (totalDiscountAmount / discountInfo.subtotal) * 100;

          // If it's a round percentage, it's likely a percentage discount
          if (Math.abs(discountPercentage - Math.round(discountPercentage)) < 0.1) {
            discountInfo.type = 'percentage';
            discountInfo.percentage = Math.round(discountPercentage);
          } else {
            discountInfo.type = 'fixed';
          }
        }
      } else {
        // Discount code exists but is not applicable to this cart
        // Try to infer discount type from the code name
        console.log('[DiscountValidator] Discount exists but not applicable - attempting to infer type from code name');

        // Extract percentage from code name if it matches pattern like "HOLIDAY30", "SAVE20", etc.
        const percentageMatch = discountCode.match(/(\d+)(?:%)?$/);
        if (percentageMatch) {
          const percentage = parseInt(percentageMatch[1]);
          if (percentage > 0 && percentage <= 100) {
            discountInfo.valid = true;
            discountInfo.type = 'percentage';
            discountInfo.percentage = percentage;
            discountInfo.amount = discountInfo.subtotal * (percentage / 100);
            console.log('[DiscountValidator] Inferred percentage discount:', percentage);
          }
        }
      }
    }

    // Calculate savings
    discountInfo.savings = discountInfo.subtotal - discountInfo.total;

    console.log('[DiscountValidator] Final discount info:', discountInfo);

    return discountInfo;
  }

  /**
   * Make a GraphQL request to Shopify Storefront API
   */
  async graphqlRequest(query, variables) {
    if (!this.storefrontAccessToken) {
      throw new Error('Storefront API access token not configured');
    }

    // Get shop domain from window.location
    const shopDomain = window.location.hostname;

    const response = await fetch(`https://${shopDomain}/api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': this.storefrontAccessToken
      },
      body: JSON.stringify({
        query,
        variables
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  /**
   * Convert Shopify Admin API ID to Global ID format
   * @param {string|number} id - The ID to convert
   * @param {string} type - The GraphQL type (e.g., 'ProductVariant', 'SellingPlan')
   */
  convertToGlobalId(id, type) {
    // If already a global ID, return it
    if (typeof id === 'string' && id.startsWith('gid://')) {
      return id;
    }

    // Convert to global ID format
    return `gid://shopify/${type}/${id}`;
  }

  /**
   * Clear the discount cache
   */
  clearCache() {
    this.cachedDiscounts.clear();
  }

  /**
   * Get a simple estimate without API call (fallback method)
   * Useful if Storefront API is not available
   */
  estimateDiscount(code, subtotal, knownDiscounts = {}) {
    const normalizedCode = code.toUpperCase();
    const discount = knownDiscounts[normalizedCode];

    if (!discount) {
      return {
        valid: false,
        estimated: true,
        code: code,
        amount: 0
      };
    }

    let amount = 0;
    if (discount.type === 'percentage') {
      amount = subtotal * (discount.value / 100);
    } else if (discount.type === 'fixed') {
      amount = Math.min(discount.value, subtotal);
    }

    return {
      valid: true,
      estimated: true,
      code: code,
      type: discount.type,
      amount: amount,
      percentage: discount.type === 'percentage' ? discount.value : null,
      subtotal: subtotal,
      total: subtotal - amount,
      savings: amount
    };
  }
}

// Create global instance
window.DiscountValidator = window.DiscountValidator || new DiscountValidator();
